import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Visualization } from './';
import { ChartMetadata, VisualizationType } from 'models/ai-chat/visualization.types';
import { FilterItem } from 'models/ai-chat/filter-request';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import { downloadChartAsCSV } from 'utils/chartDataToCSV';

interface ChartFullViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  type: VisualizationType | string;
  sqlQuery?: string;
  messageId?: string;
  metadata: ChartMetadata | null;
  onFilterValueChange?: (_filter: any, _newValue: string) => void;
  onFilterUpdateComplete?: (_updatedData: any) => void;
  filterData?: FilterItem[];
  customerId?: string;
  onExportExcel?: () => void;
  onDownloadPDF?: () => void;
}

const ChartFullViewModal: React.FC<ChartFullViewModalProps> = ({
  isOpen,
  onClose,
  data,
  type,
  sqlQuery,
  messageId,
  metadata,
  onFilterValueChange,
  onFilterUpdateComplete,
  filterData,
  customerId,
  onExportExcel,
  onDownloadPDF
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartData, setChartData] = useState(data);
  const [availableHeight, setAvailableHeight] = useState(600);

  useEffect(() => {
    if (data) {
      setChartData(data);
    }
  }, [data]);

  useEffect(() => {
    const calculateHeight = () => {
      if (!isOpen) return;
      
      // Wait for DOM to be ready
      setTimeout(() => {
        const viewportHeight = window.innerHeight;
        
        // Get actual heights of elements
        const modalHeader = document.querySelector('.modal-header');
        const filtersSection = document.querySelector('.filters-section');
        const chartContainer = document.querySelector('[data-chart-container]');
        
        const headerHeight = modalHeader ? modalHeader.getBoundingClientRect().height : 80;
        const filtersHeight = filtersSection ? filtersSection.getBoundingClientRect().height + 24 : 0; // +24 for margin
        const containerPadding = chartContainer ? 
          parseInt(getComputedStyle(chartContainer).paddingTop) + parseInt(getComputedStyle(chartContainer).paddingBottom) : 48;
        
        // Calculate modal content padding
        const modalContent = document.querySelector('.modal-content');
        const contentPadding = modalContent ? 
          parseInt(getComputedStyle(modalContent).paddingTop) + parseInt(getComputedStyle(modalContent).paddingBottom) : 32;
        
        // Safety margins for browser chrome, taskbar, etc.
        const safetyMargin = 40;
        
        // Calculate available height for chart
        const calculatedHeight = viewportHeight - headerHeight - filtersHeight - containerPadding - contentPadding - safetyMargin;
        
        // Conservative limits
        const maxHeight = Math.floor(viewportHeight * 0.65); // Never exceed 65% of viewport
        const minHeight = 300;
        
        const finalHeight = Math.max(Math.min(calculatedHeight, maxHeight), minHeight);
        setAvailableHeight(finalHeight);
      }, 100); // Small delay to ensure DOM is ready
    };

    calculateHeight();
    
    // Recalculate when filters change (dropdowns open/close)
    const observer = new MutationObserver(() => {
      calculateHeight();
    });
    
    // Observe changes in the filters section
    const filtersElement = document.querySelector('.filters-section');
    if (filtersElement) {
      observer.observe(filtersElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }
    
    window.addEventListener('resize', calculateHeight);
    
    return () => {
      window.removeEventListener('resize', calculateHeight);
      observer.disconnect();
    };
  }, [isOpen, sqlQuery]);

  const handleChartExport = () => {
    console.log('Chart export - chartData:', chartData); // Debug log
    
    if (chartData) {
      // Ensure chartData has the correct structure for CSV export
      const exportData = {
        categories: chartData.categories || [],
        series: chartData.series || [],
        title: chartData.title || 'Chart Data',
        xAxisTitle: chartData.xAxisTitle || 'Category',
        yAxisTitle: chartData.yAxisTitle || 'Value'
      };
      
      console.log('Chart export - exportData:', exportData); // Debug log
      downloadChartAsCSV(exportData);
    } else if (onExportExcel) {
      onExportExcel();
    } else {
      console.warn('No chart data available for export');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col max-h-screen overflow-hidden">
      {/* Modal Header */}
      {/* <div className="modal-header flex justify-between items-center p-4 border-b border-gray-200 bg-white shadow-sm">
        <div>
          <h3 className="text-ai80 font-semibold text-gray80">Chart Full View</h3>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 text-gray-400 hover:text-gray-600 text-2xl font-bold hover:bg-gray-100 rounded-full flex items-center justify-center"
        >
          ×
        </button>
      </div> */}
      
      {/* Modal Content */}
      <div className="modal-content flex-1 overflow-y-auto px-6 pb-12 my-5">
        <div className="w-full h-full flex flex-col">
          {/* Applied Filters */}
          {sqlQuery && (
            <div className="filters-section mb-6 flex-shrink-0">
              <AppliedFiltersDisplay
                sqlQuery={sqlQuery}
                onFilterValueChange={onFilterValueChange}
                onFilterUpdateComplete={onFilterUpdateComplete}
                customerId={customerId}
                filterData={filterData}
                messageId={messageId}
                hasChartSwitcher={false}
                pdfGeneration={metadata?.pdf_generation || false}
                onExportExcel={onExportExcel}
                onDownloadPDF={onDownloadPDF}
                chartData={chartData}
                chartType={type}
                chartMetadata={metadata}
                onFullView={() => {
                  onClose();
                  // Notify parent component that modal is closing
                  const event = new CustomEvent('chartModalClose');
                  document.dispatchEvent(event);
                }}
                isInModal={true}
              />
            </div>
          )}

          {/* Chart Container */}
          <div 
            ref={chartContainerRef}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col"
            data-chart-container
          >
            <div className="w-full flex-1">
              <Visualization
                data={chartData}
                type={type}
                sqlquery={sqlQuery}
                messageId={messageId}
                metadata={metadata}
                onFilterValueChange={onFilterValueChange}
                onFilterUpdateComplete={onFilterUpdateComplete}
                filterData={filterData}
                customerId={customerId}
                onExportExcel={onExportExcel}
                onDownloadPDF={onDownloadPDF}
                containerHeight={availableHeight}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Footer */}
      {/* <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200 bg-white shadow-sm">
        <div className="flex items-center space-x-4">
          <span className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors">
            Chart Visualization
          </span>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleChartExport}
            className="custom-border flex items-center px-4 py-1 text-xs text-primary hover:bg-[#e6e6e6] rounded transition-colors"
            title="Export to Excel"
          >
            Export
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1 bg-primary text-white text-xs rounded hover:bg-[#0041a6] transition-colors"
          >
            Close
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default ChartFullViewModal;
